import {
  BarChart3,
  Workflow,
  ClipboardCheck,
  FlaskConical,
  LayoutDashboard,
  LayoutGrid,
  ListX,
  type LucideIcon,
  OctagonAlert,
  PlusCircle,
  Send,
  ShieldAlert,
  ShieldCheck,
  Telescope,
  Zap
} from 'lucide-react';
import {type AdminSectionSlug, adminSection, adminSectionPath} from '@/lib/adminSections';
import {READ_REPORTS_PERMISSION} from '@/context/useAuth';

export type NavItem = {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly adminOnly?: boolean;
  readonly broadViewOnly?: boolean;
  // Shown only when the token carries this permission, whatever the role (see PermissionRoute).
  readonly permission?: string;
  // A page that only shows its data; the sidebar marks it with a lock.
  readonly readOnly?: boolean;
  // Planned but not built: drawn disabled with a "Soon" tag, and `to` is never routed.
  readonly soon?: boolean;
  // A fixed list rather than a setting someone edits. Settings draws a divider above the first one.
  readonly reference?: boolean;
};

export type NavGroup = {
  // Empty for the unlabelled home group at the top of the sidebar.
  readonly label: string;
  readonly items: readonly NavItem[];
  // Folded until opened, unless the current page is in it.
  readonly collapsedByDefault?: boolean;
};

function adminItem(slug: AdminSectionSlug, reference = false): NavItem {
  const section = adminSection(slug)!;
  const item: NavItem = {
    to: adminSectionPath(slug),
    label: section.label,
    icon: section.icon,
    readOnly: section.readOnly === true,
    reference
  };
  return section.broadViewOnly ? {...item, broadViewOnly: true} : {...item, adminOnly: true};
}

// The sidebar's groups. Role gating is data here and a filter below, so the route guards in App.tsx
// and this list can be compared side by side.
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: '',
    items: [{to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard}]
  },
  {
    label: 'Work',
    items: [
      {to: '/submission', label: 'Submission', icon: PlusCircle},
      {to: '/investigations', label: 'Investigations', icon: Telescope},
      {to: '/content', label: 'Content', icon: LayoutGrid},
      {to: '/queues', label: 'Queue', icon: ShieldAlert},
      {to: '/submissions', label: 'Submissions', icon: Send},
      {to: '/p0-alerts', label: 'P0 Alerts', icon: OctagonAlert}
    ]
  },
  {
    label: 'Quality',
    items: [
      {to: '/weekly-qa', label: 'Weekly QA', icon: ClipboardCheck, broadViewOnly: true},
      {to: '/qc-review', label: 'QC Review', icon: ShieldCheck, broadViewOnly: true},
      // Every analyst's own QC rejections and QA corrections, so no role gate.
      {to: '/rejected-submissions', label: 'Rejected Submissions', icon: ListX}
    ]
  },
  {
    label: 'Insights',
    items: [
      // Reports given their own rows: the submissions overview every lead and analyst starts from,
      // and how the moderation queue is being worked.
      {
        to: '/reporting/queue_analytics',
        label: 'Queue analytics',
        icon: Zap,
        permission: READ_REPORTS_PERMISSION
      },
      {
        to: '/reporting/submissions_overview',
        label: 'Submissions dashboard',
        icon: LayoutDashboard,
        permission: READ_REPORTS_PERMISSION
      },
      {to: '/reporting', label: 'Reporting', icon: BarChart3, permission: READ_REPORTS_PERMISSION},
      adminItem('feedback')
    ]
  },
  {
    // Settings that change who gets what, then the lists classification draws on. One section is
    // one page at /admin/<slug>, rather than a single Admin page with its own inner nav.
    label: 'Settings',
    collapsedByDefault: true,
    items: [
      adminItem('users'),
      adminItem('delivery'),
      adminItem('queues'),
      adminItem('escalation'),
      adminItem('customer-feedback-sheets'),
      {to: '/collections', label: 'Collections', icon: FlaskConical, adminOnly: true},
      adminItem('switches'),
      // Managed in the app, unlike the read-only lists below them.
      adminItem('topics'),
      {to: '/admin/workflows', label: 'Risk guidance', icon: Workflow},
      adminItem('classifications'),
      adminItem('languages', true),
      adminItem('core-risks', true),
      adminItem('locations', true),
      adminItem('prompts', true)
    ]
  }
];

export function visibleNavGroups(
  role: string | undefined,
  permissions: readonly string[] = [],
  queuesEnabled = false
): NavGroup[] {
  const isAdmin = role === 'admin';
  const hasBroadView = isAdmin || role === 'analyst_admin';
  const hasQueueAccess = permissions.includes('access:queues');
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter(
        (item) =>
          (!item.adminOnly || isAdmin) &&
          (!item.broadViewOnly || hasBroadView) &&
          (item.permission === undefined || permissions.includes(item.permission)) &&
          (item.to !== '/queues' || hasQueueAccess)
      )
      .map((item) => (item.to === '/queues' && !queuesEnabled ? {...item, soon: true} : item))
  })).filter((group) => group.items.length > 0);
}

// A nav entry is active for its own route and anything nested under it (/jobs/42 lights up Jobs).
// When another entry nests under it and matches too (Submissions dashboard under Reporting), only
// that more specific entry lights up.
export function isNavItemActive(
  pathname: string,
  to: string,
  others: readonly NavItem[] = []
): boolean {
  const matches = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  return matches(to) && !others.some((other) => other.to.length > to.length && matches(other.to));
}
